import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Autocomplete, Button, CircularProgress, TextField } from '@mui/material';
import { Controller, useForm, useFormState, useWatch } from 'react-hook-form';
import type { input } from 'zod';

import type { DialogType } from '../../../@types';
import type { AwardGgaFreeSpinsRequest, CasinoUserRow, GgaFreeSpinGameOption } from '../../../@types/casino';
import { useToast } from '../../../contexts/ToastContext';
import { useToastErrorMessage } from '../../../hooks/useToastErrorMessage';
import { useAwardGgaFreeSpins, useGgaFreeSpinOptions } from '../../../queries/casino';
import useGgaFreeSpinBetSelection from '../../../hooks/useGgaFreeSpinBetSelection';
import { ggaFreeSpinsSchema, type GgaFreeSpinsFormData } from '../../../schema';
import FormModalLayout from '../../layouts/FormModalLayout';
import FormNumberInput from '../../molecules/FormNumberInput';
import GgaBetLevelField from '../../molecules/GgaBetLevelField';

const DEFAULT_FORM_VALUES: GgaFreeSpinsFormData = {
  gameId: '',
  freespinAmount: null,
  betAmount: null,
};

const getGameLabel = (game: GgaFreeSpinGameOption) =>
  game.providerName ? `${game.name} (${game.providerName})` : game.name;

type GgaFreeSpinsFormProps = DialogType & {
  item: CasinoUserRow;
};

const GgaFreeSpinsForm = ({ closeModal, item: player }: GgaFreeSpinsFormProps) => {
  const { enqueueSnackbar } = useToast();
  const { handleErrorMessage } = useToastErrorMessage();
  const gameOptionsQuery = useGgaFreeSpinOptions();
  const awardFreeSpins = useAwardGgaFreeSpins();
  const gameOptions = gameOptionsQuery.data ?? [];

  const form = useForm<input<typeof ggaFreeSpinsSchema>, unknown, GgaFreeSpinsFormData>({
    defaultValues: DEFAULT_FORM_VALUES,
    resolver: zodResolver(ggaFreeSpinsSchema, undefined, { mode: 'sync' }),
    mode: 'onChange',
    disabled: awardFreeSpins.isPending,
  });

  const [gameId, betAmount] = useWatch({ control: form.control, name: ['gameId', 'betAmount'] });
  const { isValid } = useFormState({ control: form.control });
  const { isLevelGame, betLevels, invalidLevel } = useGgaFreeSpinBetSelection(
    gameOptions.find((game) => game.id === gameId),
    betAmount
  );

  const onSubmit = (data: GgaFreeSpinsFormData) => {
    if (invalidLevel) return;
    const payload: AwardGgaFreeSpinsRequest = {
      userId: player._id,
      gameId: Number(data.gameId),
      freespinAmount: data.freespinAmount as number,
      betAmount: data.betAmount as number,
    };

    awardFreeSpins.mutate(payload, {
      onSuccess: (response) => {
        enqueueSnackbar({ message: response.message || 'Free spins awarded successfully.', variant: 'success' });
        closeModal?.();
      },
      onError: handleErrorMessage,
    });
  };

  const disableSubmit =
    gameOptionsQuery.isLoading ||
    gameOptionsQuery.isError ||
    gameOptions.length === 0 ||
    awardFreeSpins.isPending ||
    !isValid ||
    invalidLevel;

  return (
    <FormModalLayout
      label='Give free spins'
      onSave={form.handleSubmit(onSubmit)}
      onClose={closeModal}
      actionButtonLabel='Give free spins'
      showEditTabs={false}
      disableSubmit={disableSubmit}
    >
      <TextField label='Player' value={player.username ?? player._id} disabled fullWidth />

      {gameOptionsQuery.isError && (
        <Alert
          severity='error'
          action={
            <Button color='inherit' size='small' onClick={() => gameOptionsQuery.refetch()}>
              Retry
            </Button>
          }
        >
          Unable to load GGA free-spin games.
        </Alert>
      )}

      {!gameOptionsQuery.isLoading && !gameOptionsQuery.isError && gameOptions.length === 0 && (
        <Alert severity='warning'>GGA did not return any games that support free spins.</Alert>
      )}

      <Controller
        name='gameId'
        control={form.control}
        render={({ field, fieldState }) => {
          const selectedGame = gameOptions.find((game) => game.id === field.value) ?? null;

          return (
            <Autocomplete
              options={gameOptions}
              value={selectedGame}
              onChange={(_event, game) => {
                field.onChange(game?.id ?? '');
                form.setValue('betAmount', null, { shouldValidate: true });
              }}
              onBlur={field.onBlur}
              getOptionLabel={getGameLabel}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={gameOptionsQuery.isLoading}
              disabled={gameOptionsQuery.isError}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Game'
                  required
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  slotProps={{
                    ...params.slotProps,
                    input: {
                      ...params.slotProps.input,
                      endAdornment: (
                        <>
                          {gameOptionsQuery.isLoading && <CircularProgress color='inherit' size={20} />}
                          {params.slotProps.input.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
          );
        }}
      />

      <FormNumberInput name='freespinAmount' control={form.control} label='Number of free spins' required fullWidth />
      {isLevelGame ? (
        <Controller
          name='betAmount'
          control={form.control}
          render={({ field, fieldState }) => (
            <GgaBetLevelField
              betLevels={betLevels}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              disabled={awardFreeSpins.isPending}
              error={fieldState.error?.message}
            />
          )}
        />
      ) : (
        <FormNumberInput
          name='betAmount'
          control={form.control}
          label='Bet amount per spin'
          allowDecimals
          decimalPlaces={2}
          valueAsString
          slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          required
          fullWidth
        />
      )}
    </FormModalLayout>
  );
};

export default GgaFreeSpinsForm;
